import { useContext, useEffect, useState } from 'react'
import Button from '../../../components/Button'
import { NavigationContext, Pages } from '../../../providers/navigation'
import { FlowContext } from '../../../providers/flow'
import Padded from '../../../components/Padded'
import ButtonsOnBottom from '../../../components/ButtonsOnBottom'
import Details, { DetailsProps } from '../../../components/Details'
import ErrorMessage from '../../../components/Error'
import { WalletContext } from '../../../providers/wallet'
import { AspContext } from '../../../providers/asp'
import { SwapsContext } from '../../../providers/swaps'
import Header from '../../../components/Header'
import { defaultFee } from '../../../lib/constants'
import { prettyNumber } from '../../../lib/format'
import Content from '../../../components/Content'
import FlexCol from '../../../components/FlexCol'
import { InsufficientFundsError, maxCollaborativeExit, sendOffChain } from '../../../lib/asp'
import {
  ASSET_RAIL,
  LNURL_ARKADE_RAIL,
  ONCHAIN_ROUTE_LOG,
  ONCHAIN_SWAP_RAIL,
  fundedResult,
  isLnurlRail,
  previewOnchainCost,
  quoteIsForThisInvoice,
  quoteIsForThisSend,
  withinPricingBudget,
} from '../../../lib/sendRouter'
import { extractError } from '../../../lib/error'
import LoadingLogo from '../../../components/LoadingLogo'
import { consoleError, consoleLog } from '../../../lib/logs'
import type { RouteQuote } from '@arkade-os/sdk'
import { lnurlQuoteMeta } from '@arkade-os/lnurl-client/arkade'
import { LimitsContext } from '../../../providers/limits'
import { FeesContext } from '../../../providers/fees'
import { buildTransactionAmountDisplay } from '../../../lib/transactionAmountDisplay'
import { useAmountDisplayContext } from '../../../hooks/useTransactionAmountDisplay'
import TransactionAmountSummary from '../../../components/TransactionAmountSummary'
import { saveTransactionActivityMetadata } from '../../../lib/storage'
import { lnurlSends, recordLnurlSend, watchLnurlConfirmation } from '../../../lib/lnurlSends'
import { useTranslation } from '../../../providers/language'

export default function SendDetails() {
  const displayContext = useAmountDisplayContext()
  const { navigate } = useContext(NavigationContext)
  const { appIntent, sendInfo, setAppIntent, setSendInfo } = useContext(FlowContext)
  const { calcOnchainOutputFee, intentFee } = useContext(FeesContext)
  const isAssetSend = Boolean(sendInfo.account || sendInfo.assets?.length)
  const { utxoTxsAllowed, vtxoTxsAllowed } = useContext(LimitsContext)
  const { assetMetadataCache, availableBalance, balance, reloadWallet, svcWallet } = useContext(WalletContext)
  const { aspInfo } = useContext(AspContext)
  const { sendRouter } = useContext(SwapsContext)
  const { t } = useTranslation()

  const assetId = sendInfo.account?.assetId ?? sendInfo.assets?.[0]?.assetId
  const assetMeta = assetId ? assetMetadataCache.get(assetId) : undefined
  const assetDecimals = sendInfo.account?.decimals ?? assetMeta?.metadata?.decimals ?? 0
  const assetAmountValue = sendInfo.account?.amount ?? sendInfo.assets?.[0]?.amount ?? BigInt(0)

  const [buttonLabel, setButtonLabel] = useState('')
  const [details, setDetails] = useState<DetailsProps>()
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [pricing, setPricing] = useState(false)
  const [sending, setSending] = useState(false)
  const [sendDone, setSendDone] = useState(false)

  const { address, arkAddress, invoice, lnUrl, pendingLnSend, satoshis } = sendInfo
  const lnurlRoute = Boolean(lnUrl && pendingLnSend && isLnurlRail(pendingLnSend.railId))
  const amountDisplay = buildTransactionAmountDisplay({
    ...displayContext,
    assets: sendInfo.account
      ? [{ assetId: sendInfo.account.assetId, amount: sendInfo.account.amount }]
      : sendInfo.assets,
    metadataForAsset: (id) => {
      const metadata = assetMetadataCache.get(id)?.metadata
      return id === assetId ? { ...metadata, decimals: assetDecimals } : metadata
    },
    satoshis: details?.satoshis ?? satoshis ?? 0,
  })

  const offerToSign = (total: number) => {
    if (balance < total) {
      setButtonLabel(t('send.insufficientFunds'))
      setError(t('send.insufficientFundsDetail', { balance: prettyNumber(balance) }))
    } else {
      setButtonLabel(t('send.tapToSign'))
    }
  }

  useEffect(() => {
    if (!address && !arkAddress && !invoice && !lnurlRoute) return setError(t('send.missingAddress'))
    if (isAssetSend) {
      if (!assetAmountValue) return setError(t('send.missingAssetAmount'))
      const destination = arkAddress ?? ''
      const feeInSats = defaultFee
      setDetails({
        assetId,
        destination,
        direction: t('send.directionSendingAssets'),
        fees: feeInSats,
        satoshis: 0,
        total: feeInSats,
      })
      setButtonLabel(t('send.tapToSign'))
      setError('')
      return
    }
    if (!satoshis) return setError(t('send.missingAmount'))
    const destination =
      lnurlRoute && vtxoTxsAllowed()
        ? lnUrl!
        : arkAddress && vtxoTxsAllowed()
          ? arkAddress
          : invoice && pendingLnSend && vtxoTxsAllowed()
            ? invoice
            : address && utxoTxsAllowed()
              ? address
              : ''
    // Routing is a protocol decision; it must never depend on a localized string,
    // otherwise fee math and labels drift when the language changes.
    const destinationType =
      lnurlRoute && destination === lnUrl
        ? pendingLnSend?.railId === LNURL_ARKADE_RAIL
          ? 'arkade'
          : 'lightning'
        : destination === arkAddress
          ? 'arkade'
          : destination === invoice
            ? 'lightning'
            : destination === address
              ? 'mainnet'
              : 'none'
    // `direction` is display-only; keep logic keyed on `destinationType`.
    const direction =
      destinationType === 'arkade'
        ? t('send.payingInsideArkade')
        : destinationType === 'lightning'
          ? t('send.payingToLightning')
          : destinationType === 'mainnet'
            ? t('send.payingToMainnet')
            : ''

    // The RFQ lockup carries exactly the invoice amount (exact-out, fee_bps
    // from the card; 0 today), so total == satoshis on the Lightning path.
    const total = pendingLnSend ? pendingLnSend.total : satoshis
    const amount = destinationType === 'mainnet' ? satoshis - calcOnchainOutputFee() : satoshis
    const fees = total - amount > 0 ? total - amount : 0
    setDetails({
      destination,
      direction,
      fees,
      satoshis: amount,
      total,
    })
    // Provisional on this path until the router settles it below.
    if (destinationType === 'mainnet' && amount > 0) {
      setButtonLabel(t('send.gettingQuote'))
      return setPricing(true)
    }
    offerToSign(total)
  }, [sendInfo])

  useEffect(() => {
    if (!pricing || !details?.destination || !details.satoshis) return
    let live = true
    const asked = details.satoshis
    type Priced = { cost: Pick<RouteQuote, 'amount' | 'fee' | 'total'>; most?: number }
    const settle = (priced?: Priced) => {
      if (!live) return
      const cost = priced?.cost
      if (cost)
        setDetails((prev) => (prev ? { ...prev, satoshis: cost.amount, fees: cost.fee, total: cost.total } : prev))
      if (cost && cost.amount < asked) setNotice(t('send.feesDeductedFromAmount'))
      setPricing(false)
      if (priced?.most === undefined) return offerToSign(cost?.total ?? details.total ?? 0)
      setButtonLabel(t('send.insufficientFunds'))
      setError(t('send.insufficientFundsMax', { max: prettyNumber(priced.most) }))
    }
    const priced = sendRouter({ outputFee: calcOnchainOutputFee, intentFee })
      .then(async (router): Promise<Priced | undefined> => {
        const cost = await previewOnchainCost(router, details.destination!, asked)
        if (cost || !svcWallet) return cost && { cost }
        const most = (await maxCollaborativeExit(svcWallet, intentFee(), Number(aspInfo.dust))) - calcOnchainOutputFee()
        const atMost =
          most > 0 && most < asked ? await previewOnchainCost(router, details.destination!, most) : undefined
        if (!atMost) return undefined
        // Max asks for the whole balance, so the fees can only come out of the amount, as the exit fee does.
        if (satoshis === availableBalance) return { cost: atMost }
        // Any other amount stays as asked, at the fee of spending every coin: more than they hold.
        return { cost: { amount: asked, fee: atMost.fee, total: asked + atMost.fee }, most }
      })
      .catch((err) => {
        // The exit rail's own figures stay on screen, and it is what will pay.
        consoleError(err, `${ONCHAIN_ROUTE_LOG} could not price the send`)
        return undefined
      })
    withinPricingBudget(priced)
      .then(settle)
      .catch((err) => consoleError(err, `${ONCHAIN_ROUTE_LOG} could not price the send`))
    return () => {
      live = false
    }
  }, [pricing, details?.destination, details?.satoshis])

  const handleTxid = (txid: string) => {
    if (!txid) return handleError(t('send.errorSendingTransaction'))
    saveTransactionActivityMetadata(txid, {
      destination: details?.destination,
      networkFee: details?.fees,
    })
    // Refresh now instead of waiting on the worker's VTXO_UPDATE broadcast:
    // that message rides on the indexer subscription, and for a transaction
    // this wallet submitted itself it can arrive late or not at all, leaving
    // the balance and the history a payment behind until a manual refresh.
    // The worker persists the spent inputs and the change VTXO before it hands
    // back the txid, so its cache is already current — and the metadata saved
    // just above is what the refreshed history grafts onto this row.
    reloadWallet().catch(consoleError)
    setSendInfo({ ...sendInfo, total: details?.total, txid })
    setSendDone(true)
  }

  /** Unlike {@link handleTxid} a missing txid is not an error: the solver rail
   *  commits by funding. The fee comes off the QUOTE, not the screen — a rail
   *  may charge less than was displayed.
   *
   *  No fallback key: the store and every row builder are keyed BY the funding
   *  txid, so anything else would never be looked up. What made this miss was
   *  WHEN it ran — on the terminal outcome, minutes after the user had gone. */
  const handleSent = (txid: string | undefined, total: number, fee: number, railId: string, swapId?: string) => {
    if (txid) {
      saveTransactionActivityMetadata(txid, { destination: details?.destination, networkFee: fee })
    }
    reloadWallet().catch(consoleError)
    if (
      railId === ONCHAIN_SWAP_RAIL &&
      appIntent?.status === 'send' &&
      appIntent.callback &&
      sendInfo.recipient === appIntent.request &&
      !appIntent.committedOnchain
    ) {
      setAppIntent({ ...appIntent, committedOnchain: { swapId, fundingTxid: txid } })
    }
    setSendInfo({ ...sendInfo, total, txid, railId, swapId })
    setSendDone(true)
  }

  const handleExitComplete = () => {
    if (error) return setSending(false)
    else navigate(Pages.SendSuccess)
  }

  const handleError = (err: any) => {
    consoleError(err, 'error sending payment')
    setError(extractError(err))
    setSendDone(true)
  }

  /** Fund the covenant, then return; both swap rails end here. Funding IS
   *  acceptance, so waiting for the TERMINAL outcome only made the user watch
   *  the counterparty's pipeline — the swaps provider observes that and
   *  outlives this screen. An on-chain send must still claim its L1 HTLC, which
   *  is why the success copy says "on the way" for both rails. The invoice is
   *  re-checked because the quote came from the previous screen. */
  const payLightning = async (quote: RouteQuote, shownInvoice: string) => {
    if (!quoteIsForThisInvoice(quote, shownInvoice)) return handleError(t('send.quoteForDifferentInvoice'))
    const result = await fundedResult(await quote.send())
    handleSent(result?.txid, quote.total, quote.fee, quote.railId, result?.swapId)
  }

  /** The quote came from the previous screen, so it is held to what this one shows. */
  const payLnurl = async (quote: RouteQuote, target: string) => {
    const lnurlMeta = lnurlQuoteMeta(quote)
    if (lnurlMeta?.target !== target || quote.amount !== satoshis)
      return handleError(t('send.quoteForDifferentPayment'))
    const handle = await quote.send()
    recordLnurlSend(handle, quote, target)
    const result = await fundedResult(handle)
    // A rail whose destination cannot identify the payment supplies no verify
    // URL, and absence is "no answer available" rather than a failure.
    if (result?.txid && lnurlMeta.verify)
      watchLnurlConfirmation(
        result.txid,
        lnurlMeta.verify,
        lnurlMeta.verifyBatch,
        lnurlSends().find((sent) => sent.txid === result.txid)?.createdAt,
        lnurlMeta.expect,
      )
    handleSent(result?.txid, quote.total, quote.fee, quote.railId)
  }

  /** One rail and no counterparty; routed so every branch here has one shape. */
  const payAssets = async (arkAddress: string, assets: NonNullable<typeof sendInfo.assets>) => {
    const router = await sendRouter({ assets })
    const options = await router.options({ raw: arkAddress })
    const route = options.find((option) => option.railId === ASSET_RAIL)
    if (!route) throw new Error('No route for this payment')
    const quote = await route.quote()
    const result = await (await quote.send()).settled()
    handleTxid(result.txid ?? '')
  }

  /** Pay an L1 address through the router. The request is built from `address` —
   *  what THIS screen is showing — at the moment of the spend, so no carried
   *  quote exists to go stale. A rail that cannot quote is skipped. */
  const payOnchain = async (address: string, shown: DetailsProps): Promise<void> => {
    const router = await sendRouter({ outputFee: calcOnchainOutputFee, intentFee })
    // Receiver-exact: "what leaves" was subtracted when `details` was built.
    const options = await router.options({ raw: address, amount: shown.satoshis! })
    let repriced: RouteQuote | undefined
    let insufficient = false
    let otherwiseRefused = false

    for (const option of options) {
      let quote
      try {
        quote = await option.quote()
      } catch (err) {
        consoleError(err, `${ONCHAIN_ROUTE_LOG} ${option.railId} could not quote`)
        if (err instanceof InsufficientFundsError) insufficient = true
        else otherwiseRefused = true
        continue
      }
      if (!quoteIsForThisSend(quote, shown, address)) {
        if (quoteIsForThisSend({ ...quote, total: shown.total ?? 0 }, shown, address)) repriced ??= quote
        else otherwiseRefused = true
        consoleLog(
          `${ONCHAIN_ROUTE_LOG} ${option.railId} refused: quote pays ${quote.amount} for ${quote.total}, ` +
            `screen shows ${shown.satoshis} for ${shown.total} to ${shown.destination}`,
        )
        continue
      }
      consoleLog(`${ONCHAIN_ROUTE_LOG} paying via ${option.railId}`)
      // Deliberately NOT caught, and the funding is why: this reaches
      // `ServiceWorkerWallet.send`, whose worker submits the Ark tx and only
      // then replies (#949), so a rejection here can mean a covenant that IS
      // funded. Trying the exit rail next would pay the recipient twice.
      const result = await fundedResult(await quote.send())
      return handleSent(result?.txid, quote.total, quote.fee, option.railId, result?.swapId)
    }
    if (repriced) return askToSignAgain(repriced)
    if (insufficient && !otherwiseRefused) throw new Error(t('send.insufficientFunds'))
    throw new Error('No route for this payment')
  }

  /** A rail that now costs more is shown, never paid: the new total needs its own tap. */
  const askToSignAgain = ({ fee, total }: Pick<RouteQuote, 'fee' | 'total'>) => {
    setDetails((prev) => (prev ? { ...prev, fees: fee, total } : prev))
    setNotice(t('send.feeChanged'))
    offerToSign(total)
    setSending(false)
  }

  const handleContinue = async () => {
    if (!details || !svcWallet || pricing) return
    if (!isAssetSend && (!details.total || !details.satoshis)) return
    if (isAssetSend && !arkAddress) {
      setError(t('send.assetsOnlyToArkade'))
      return
    }

    setNotice('')
    setSending(true)

    if (isAssetSend && arkAddress) {
      if (!sendInfo.assets || sendInfo.assets.length === 0) return handleError('Missing assets list')
      payAssets(arkAddress, sendInfo.assets).catch(handleError)
    } else if (lnurlRoute && details.destination === lnUrl) {
      payLnurl(pendingLnSend!, lnUrl!).catch(handleError)
    } else if (lnurlRoute && !address) {
      return handleError(t('send.sendingOffchainNotAllowed'))
    } else if (arkAddress) {
      if (!details.total) return handleError(t('send.missingTotalAmount'))
      sendOffChain(svcWallet, details.total, arkAddress)
        .then((txId: string) => handleTxid(txId))
        .catch(handleError)
    } else if (invoice && pendingLnSend) {
      // RFQ Lightning send. The address the rail funds is the wallet's OWN
      // derivation of the lockup covenant (the client refuses a mismatched
      // quote), so funding it IS the acceptance — no further message exists.
      payLightning(pendingLnSend, invoice).catch(handleError)
    } else if (address) {
      if (!details.total) return handleError(t('send.missingTotalAmount'))
      if (!details.satoshis) return handleError(t('send.missingSatoshisAmount'))
      // Blanked by the limit, not by routing: every rail fails `quoteIsForThisSend`.
      if (!details.destination) return handleError('On-chain sends are not permitted on this account')
      payOnchain(address, details).catch(handleError)
    }
  }

  return (
    <>
      <Header text={t('send.signTransaction')} back />
      <Content>
        {sending ? (
          <LoadingLogo
            text={
              lnurlRoute && details?.destination === lnUrl
                ? details?.direction || 'Paying'
                : details?.destination === invoice
                  ? t('send.payingToLightning')
                  : details?.destination === arkAddress
                    ? t('send.payingInsideArkade')
                    : t('send.payingToMainnet')
            }
            done={sendDone}
            exitMode='fly-up'
            onExitComplete={handleExitComplete}
          />
        ) : (
          <Padded>
            <FlexCol>
              <ErrorMessage error={Boolean(error || notice)} text={error || notice} />
              {details && amountDisplay ? (
                <TransactionAmountSummary amount={amountDisplay} label={t('send.amountToSend')} />
              ) : null}
              <Details
                details={
                  details ? { ...details, amountDisplay, total: isAssetSend ? undefined : details.total } : undefined
                }
              />
            </FlexCol>
          </Padded>
        )}
      </Content>
      <ButtonsOnBottom>
        {sending ? null : <Button onClick={handleContinue} label={buttonLabel} disabled={Boolean(error) || pricing} />}
      </ButtonsOnBottom>
    </>
  )
}
